import React, { useState, useContext, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { Row, Col, Form, Spinner } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faThumbtack } from '@fortawesome/free-solid-svg-icons'
import AuthContext from 'context/auth-context'
import customColumnService from '../../services/customColumnService'
import MutedText from 'shared/forms/muted-text'

const CustomColumnsTab = ({ transactionId, dashboardId, onPinField, pinnedFieldIds, canEdit = true }) => {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()
  const [editingValues, setEditingValues] = useState({})
  const isInitializedRef = useRef(false)

  // Fetch custom columns for the dashboard
  const { data: customColumns = [], isLoading: columnsLoading } = useQuery(
    ['customColumns', dashboardId],
    () => customColumnService.getCustomColumns(token, dashboardId),
    {
      enabled: !!dashboardId && !!token
    }
  )

  // Fetch custom column values for this transaction
  const { data: customColumnValuesData = {}, isLoading: valuesLoading } =
    useQuery(
      ['customColumnValues', dashboardId, transactionId],
      () =>
        customColumnService.getCustomColumnValues(token, dashboardId, [
          transactionId
        ]),
      {
        enabled: !!dashboardId && !!token && !!transactionId
      }
    )

  // Get values for this transaction - memoized to prevent unnecessary re-renders
  const transactionValues = useMemo(() => {
    return customColumnValuesData[transactionId] || {}
  }, [customColumnValuesData, transactionId])

  // Track if values have been loaded to avoid initializing with empty data
  const valuesLoadedRef = useRef(false)

  // Mark values as loaded when we get actual data
  useEffect(() => {
    if (Object.keys(customColumnValuesData).length > 0) {
      valuesLoadedRef.current = true
    }
  }, [customColumnValuesData])

  // Reset values loaded flag when transaction changes
  useEffect(() => {
    valuesLoadedRef.current = false
  }, [transactionId])

  // Initialize editing values when both columns and values are ready
  // Don't re-initialize when data refetches (would overwrite user edits)
  useEffect(() => {
    // Wait until both columns are loaded AND either:
    // 1. Values have been loaded (valuesLoadedRef is true), OR
    // 2. Values query has completed but returned empty (valuesLoading is false)
    const valuesReady = valuesLoadedRef.current || !valuesLoading

    if (customColumns.length > 0 && valuesReady && !isInitializedRef.current) {
      const initialValues = {}
      customColumns.forEach((column) => {
        const valueData = transactionValues[column.id]
        initialValues[column.id] = valueData?.value || ''
      })
      setEditingValues(initialValues)
      isInitializedRef.current = true
    }
  }, [customColumns, transactionValues, valuesLoading])

  // Reset initialization flag when transaction changes
  useEffect(() => {
    isInitializedRef.current = false
  }, [transactionId])

  // Sync editingValues when transactionValues change (e.g., from PinnedFieldsCard updates)
  useEffect(() => {
    if (isInitializedRef.current && Object.keys(transactionValues).length > 0) {
      setEditingValues((prev) => {
        const updated = { ...prev }
        customColumns.forEach((column) => {
          const valueData = transactionValues[column.id]
          if (valueData !== undefined) {
            updated[column.id] = valueData?.value || ''
          }
        })
        return updated
      })
    }
  }, [transactionValues, customColumns])

  // Mutation for saving a single custom column value
  const saveMutation = useMutation(
    (data) =>
      customColumnService.upsertCustomColumnValue(token, dashboardId, data),
    {
      onSuccess: () => {
        // Invalidate queries to refresh data and keep pinned and tab values in sync
        queryClient.invalidateQueries(['customColumnValues', dashboardId])
        queryClient.invalidateQueries(['customColumnValues', dashboardId, transactionId])
        queryClient.invalidateQueries(['transactions'])
      },
      onError: (error) => {
        console.error('Error saving custom column value:', error)
        alert('Failed to save custom column value. Please try again.')
      }
    }
  )

  const handleValueChange = (columnId, value) => {
    setEditingValues((prev) => ({
      ...prev,
      [columnId]: value
    }))
  }

  const handleSave = async (columnId) => {
    await saveMutation.mutateAsync({
      custom_column_id: columnId,
      transaction_id: transactionId,
      value: editingValues[columnId]
    })
  }

  const formatReadOnlyValue = (column, value) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-muted">-</span>
    }

    switch (column.column_type) {
      case 'boolean':
        return value === 'true' || value === true ? 'Yes' : 'No'
      case 'date':
        // Format date for display
        if (value) {
          const date = new Date(value)
          return date.toLocaleDateString()
        }
        return '-'
      default:
        return value
    }
  }

  const renderEditControl = (column) => {
    const value = editingValues[column.id] || ''

    // Render read-only display when user cannot edit
    if (!canEdit) {
      return <div>{formatReadOnlyValue(column, value)}</div>
    }

    const commonProps = {
      value,
      onChange: (e) => handleValueChange(column.id, e.target.value),
      onBlur: () => handleSave(column.id),
      size: 'sm'
    }

    switch (column.column_type) {
      case 'text':
        return (
          <Form.Control
            {...commonProps}
            as="textarea"
            rows={2}
            placeholder="Enter text..."
          />
        )

      case 'number':
        return (
          <Form.Control
            {...commonProps}
            type="number"
            placeholder="Enter number..."
          />
        )

      case 'date':
        return (
          <Form.Control
            value={value}
            onChange={(e) => {
              handleValueChange(column.id, e.target.value)
              // Auto-save for date - immediately save when date is selected
              saveMutation.mutate({
                custom_column_id: column.id,
                transaction_id: transactionId,
                value: e.target.value
              })
            }}
            size="sm"
            type="date"
            placeholder="Select date..."
          />
        )

      case 'boolean':
        return (
          <Form.Check
            type="checkbox"
            checked={value === 'true' || value === true}
            onChange={(e) => {
              const newValue = e.target.checked ? 'true' : 'false'
              handleValueChange(column.id, newValue)
              // Auto-save for checkbox
              saveMutation.mutate({
                custom_column_id: column.id,
                transaction_id: transactionId,
                value: newValue
              })
            }}
          />
        )

      case 'select':
        const selectOptions = column.settings?.options || []
        return (
          <Form.Select
            {...commonProps}
            onChange={(e) => {
              handleValueChange(column.id, e.target.value)
              // Auto-save for select
              saveMutation.mutate({
                custom_column_id: column.id,
                transaction_id: transactionId,
                value: e.target.value
              })
            }}
          >
            <option value="">Select...</option>
            {selectOptions.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </Form.Select>
        )

      case 'status':
        const statusOptions = column.settings?.options || [
          'To-do',
          'In progress',
          'Complete'
        ]
        return (
          <Form.Select
            {...commonProps}
            onChange={(e) => {
              handleValueChange(column.id, e.target.value)
              // Auto-save for status
              saveMutation.mutate({
                custom_column_id: column.id,
                transaction_id: transactionId,
                value: e.target.value
              })
            }}
          >
            <option value="">Select status...</option>
            {statusOptions.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </Form.Select>
        )

      default:
        return (
          <Form.Control
            {...commonProps}
            type="text"
            placeholder="Enter value..."
          />
        )
    }
  }

  if (columnsLoading || valuesLoading) {
    return (
      <div className="w-100 text-center py-4">
        <Spinner animation="border" role="status" size="sm">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  if (!dashboardId) {
    return (
      <div className="w-100 py-4 text-muted text-center">
        <p>
          Custom columns are only available when viewing transactions from a
          dashboard.
        </p>
      </div>
    )
  }

  if (customColumns.length === 0) {
    return (
      <div className="w-100 py-4 text-muted text-center">
        <p>No custom columns have been created for this dashboard.</p>
      </div>
    )
  }

  const isPinned = (column) => {
    return pinnedFieldIds && pinnedFieldIds.has(`custom_${column.id}`)
  }

  return (
    <div className="w-100">
      <Row>
        {customColumns
          .filter((column) => column.is_active)
          .sort((a, b) => (a.column_order || 0) - (b.column_order || 0))
          .map((column) => {
            const pinned = isPinned(column)

            return (
              <Col key={column.id} xs={12} sm={6} md={6} lg={3} className="mb-3">
                <div className="custom-column-field-item">
                  <div className="d-flex align-items-center mb-1">
                    <MutedText as="div" className="small">{column.name}</MutedText>
                    {onPinField && (
                      <span
                        className={`pin-icon ms-1 ${pinned ? 'text-primary pinned' : 'text-muted'}`}
                        title={pinned ? 'Unpin field' : 'Pin field'}
                        style={{ fontSize: '0.75rem', cursor: 'pointer' }}
                        onClick={() => onPinField(column, pinned, 'CustomColumn')}
                      >
                        <FontAwesomeIcon icon={faThumbtack} />
                      </span>
                    )}
                  </div>
                  {renderEditControl(column)}
                </div>
              </Col>
            )
          })}
      </Row>
      {saveMutation.isLoading && (
        <div className="text-muted small mt-2">
          <Spinner animation="border" size="sm" className="me-2" />
          Saving...
        </div>
      )}
      <style jsx>{`
        .custom-column-field-item {
          padding: 0.5rem;
          border-radius: 4px;
        }

        .custom-column-field-item .pin-icon {
          opacity: 0;
          transition: opacity 0.2s ease;
        }

        .custom-column-field-item .pin-icon.pinned {
          opacity: 1;
        }

        .custom-column-field-item:hover .pin-icon {
          opacity: 1;
        }
      `}</style>
    </div>
  )
}

CustomColumnsTab.propTypes = {
  transactionId: PropTypes.string.isRequired,
  dashboardId: PropTypes.string,
  onPinField: PropTypes.func,
  pinnedFieldIds: PropTypes.instanceOf(Set),
  canEdit: PropTypes.bool
}

export default CustomColumnsTab
